import { inject, Injectable } from '@angular/core';
import { LandingPageResource } from '../landing-page/landing-page.resource';
import { Credentials } from '@app/pages/main-form/forms/credentials/form/credentials-form.types';
import { IPersistenceResource } from './persistence.types';
import { Informations } from '@app/pages/main-form/forms/informations/form/informations-form.types';
import { Manual } from '@app/pages/main-form/forms/manual/form/manual-form.types';
import { RegisteredCompany } from '@infra/company';
import { AnalyticsResource } from '@infra/analytics';
import { QueryParamsResource } from '@infra/query-params';
import { StorageStrategyResource } from '@infra/storage';

@Injectable({
  providedIn: 'root',
})
export class PersistenceResource implements IPersistenceResource {
  private landingPageResource = inject(LandingPageResource);
  private analyticsResource = inject(AnalyticsResource);
  private queryParamsResource = inject(QueryParamsResource);
  private storageStrategyResource = inject(StorageStrategyResource);
  private sessionStorage = this.storageStrategyResource.create('sessionStorage');

  clearForm() {
    this.sessionStorage.removeItem('@anota-ai/initiate/registered_company');
  }

  setDownloaded(value: boolean) {
    this.sessionStorage.setItem('@anota-ai/initiate/downloaded', String(value));
  }

  getDownloaded() {
    return String(this.sessionStorage.getItem('@anota-ai/initiate/downloaded')) === 'true';
  }

  setDealId(data: Partial<Informations>) {
    this.sessionStorage.setItem('@anota-ai/initiate/deal_id', JSON.stringify(data));
  }

  setInformations(data: Partial<Informations>) {
    this.sessionStorage.setItem('@anota-ai/initiate/informations', JSON.stringify(data));
  }

  getInformations(): Partial<Informations> | null {
    const data = this.landingPageResource.getData();
    const query = this.queryParamsResource.getCurrent();

    const deal_id = query?.['deal_id'] || query?.['id_deal'] || this.getDealId()?.deal_id;

    if (data?.analytics_id) {
      this.analyticsResource.setId(data.analytics_id);
    }

    if (Object.hasOwn(query, 'lp_fallback')) {
      this.setLandingPageFallback(query['lp_fallback']);
    }

    if (Object.hasOwn(query, 'origin')) {
      this.setOriginSource(query['origin']);
    }

    if (Object.hasOwn(query, 'deal_id')) {
      this.setDealId({deal_id : query['deal_id']});
    }

    if (deal_id) {
      this.setDealId({deal_id : deal_id});
    }

    try {
      if (data) {
        data.phone = data.phone?.replace(/[^0-9]/gi, '');

        return {
          acceptTerms: data.acceptTerms,
          company: data.company,
          computer: data.computer,
          email: data.email,
          name: data.name,
          phone: data.phone,
          waiter_quantity: data.waiter_quantity!,
          order_day: data.order_day!,
          waiter_tables: data.waiter_tables!,
          deal_id: data.deal_id || this.getDealId()?.deal_id,
          ipAddress: data.ipAddress,
          force_central_account: data.force_central_account,
          force_billing_core_subscription: data.force_billing_core_subscription,
          invoicing: data.invoicing
        };
      }

      const info = JSON.parse(
        this.sessionStorage.getItem('@anota-ai/initiate/informations') || '{}',
      ) as Informations;

      info.phone = info.phone?.replace(/[^0-9]/, '');
      info.deal_id = this.getDealId()?.deal_id || info?.deal_id;
      return info;
    } catch (error) {}
    return null;
  }

  setCredentials(data: Credentials) {
    this.sessionStorage.setItem('@anota-ai/initiate/credentials', JSON.stringify(data));
  }

  getCredentials(): Credentials | null {
    try {
      return JSON.parse(this.sessionStorage.getItem('@anota-ai/initiate/credentials') || '') as Credentials;
    } catch (error) {}
    return null;
  }

  setManual(data: Manual) {
    this.sessionStorage.setItem('@anota-ai/initiate/manual', JSON.stringify(data));
  }

  getManual(): Manual | null {
    try {
      return JSON.parse(this.sessionStorage.getItem('@anota-ai/initiate/manual') || '') as Manual;
    } catch (error) {}
    return null;
  }

  getDealId() {
    try {
      return JSON.parse(this.sessionStorage.getItem('@anota-ai/initiate/deal_id') || '');
    } catch (error) {}
    return null;
  }

  setRegisteredCompany(data: RegisteredCompany) {
    this.sessionStorage.setItem('@anota-ai/initiate/registered_company', JSON.stringify(data));
  }

  getRegisteredCompany(): RegisteredCompany | null {
    try {
      return JSON.parse(
        this.sessionStorage.getItem('@anota-ai/initiate/registered_company') || '',
      ) as RegisteredCompany;
    } catch (error) {}
    return null;
  }

  setLandingPageFallback(data: string) {
    this.sessionStorage.setItem('@anota-ai/app/landing_page_fallback', data);
  }

  getLandingPageFallback(): string | null {
    try {
      return this.sessionStorage.getItem('@anota-ai/app/landing_page_fallback');
    } catch (error) {}
    return null;
  }

  setOriginSource(data: string) {
    this.sessionStorage.setItem('@anota-ai/app/origin', data);
  }

  getOriginSource(): string | null {
    try {
      return this.sessionStorage.getItem('@anota-ai/app/origin');
    } catch (error) {}
    return null;
  }
}
